import { appRoutes } from '@/constants'
import { Lock, Mail, Shield, ShieldAlert, Sparkles } from 'lucide-react'
import { motion, Variants } from 'framer-motion'

export const CTASection = () => {
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  }

  const headerVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 30,
      scale: 0.9
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  }

  const subtitleVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        delay: 0.2,
        ease: 'easeOut'
      }
    }
  }

  const cardVariants: Variants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      rotateY: -10
    },
    visible: {
      opacity: 1,
      scale: 1,
      rotateY: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  }

  const iconPulseVariants: Variants = {
    visible: {
      scale: [1, 1.2, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: 'reverse' as const,
        ease: 'easeInOut'
      }
    }
  }

  const buttonVariants: Variants = {
    hidden: {
      opacity: 0,
      scale: 0.5,
      y: 50
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.8,
        delay: 0.6,
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  }

  const glowVariants: Variants = {
    visible: {
      boxShadow: [
        '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        '0 0 50px rgba(244, 200, 106, 0.5)',
        '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
      ],
      transition: {
        duration: 2.5,
        repeat: Infinity,
        ease: 'easeInOut'
      }
    }
  }

  const faqItems = [
    {
      icon: Sparkles,
      question: 'What if I need emergency access?',
      answer:
        'There are no emergency overrides. Completing your quests is the only way to unlock your authenticator. Quests are meant to be demanding - that is the point.'
    },
    {
      icon: Shield,
      question: 'Where are my authenticator codes stored?',
      answer:
        "Your authenticator secrets are stored in your browser and never sent to our servers. When locked, they're encrypted with a key that's mathematically split across your challenges."
    },
    {
      icon: Mail,
      question: 'Can I just remove 2FA from my account?',
      answer:
        'No. Most platforms require your 2FA code to change account settings - including removing 2FA. Once you lock your authenticator, you must complete your quests to regain access.'
    },
    {
      icon: ShieldAlert,
      question: 'What if a quest becomes impossible?',
      answer:
        "That's why you create more quests than the recovery threshold. If a location closes, just embark on a different quest. You design your own safety net."
    }
  ]

  return (
    <section className="bg-shire-moss/60 py-20 text-center sm:py-40">
      <motion.div
        className="mx-auto max-w-5xl px-6"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={containerVariants}
      >
        <motion.h2
          className="font-heading mb-6 text-4xl md:text-5xl"
          variants={headerVariants}
        >
          Stop Losing Years to Scrolling
        </motion.h2>

        <motion.p
          className="text-shire-bark mx-auto mb-8 text-xl leading-relaxed"
          variants={subtitleVariants}
        >
          Every day you wait, more of your life slips away into feeds designed
          to capture your attention. Quest-Lock gives you back control by making
          access require genuine intention.
          <br />
          <motion.span
            className="text-shire-dark font-semibold"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.5 }}
          >
            Your future self will thank you.
          </motion.span>
        </motion.p>

        <motion.div
          className="mb-12 grid gap-6 md:grid-cols-2"
          variants={containerVariants}
        >
          {faqItems.map((item, index) => {
            const Icon = item.icon
            return (
              <motion.div
                key={index}
                className="bg-shire-light/70 rounded-xl p-6"
                variants={cardVariants}
                whileHover={{
                  scale: 1.03,
                  backgroundColor: 'rgba(254, 252, 248, 0.85)',
                  transition: { duration: 0.3 }
                }}
              >
                <h3 className="text-shire-dark mb-2 flex items-center justify-center gap-2 text-center font-semibold">
                  <motion.div variants={iconPulseVariants} animate="visible">
                    <Icon className="size-5" />
                  </motion.div>
                  &ldquo;{item.question}&rdquo;
                </h3>
                <p className="text-shire-bark">{item.answer}</p>
              </motion.div>
            )
          })}
        </motion.div>

        <motion.button
          className="bg-shire-sun font-ui text-shire-dark hover:bg-shire-bark hover:text-shire-light mb-6 inline-flex items-center gap-3 rounded-full px-12 py-4 text-xl font-semibold shadow-lg transition-colors duration-300"
          onClick={() => window.location.assign(appRoutes.authenticator)}
          variants={buttonVariants}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.div
            animate="visible"
            variants={glowVariants}
            className="absolute inset-0 rounded-full"
            style={{ zIndex: -1 }}
          />
          <Lock className="size-6" />
          Begin Your Quest
        </motion.button>
      </motion.div>
    </section>
  )
}
